import { useMemo, useEffect } from 'react';
import {
	IMPORT_TYPE_PARAM_KEY,
	WORKSPACE_ID_PARAM_KEY,
	ENTITY_ID_PARAM_KEY,
} from '@atlassian/jira-project-create-importer-common';
import { useQueryParam, useRouter } from '@atlassian/jira-router';
import { type ImportType, supportedImportTypesFromQueryParam } from '../../constants/import-type';

export interface ImporterContext {
	importType: ImportType;
	workspaceId: string | null;
	entityId: string | null;
}

interface ImporterContextParams {
	importTypeParam: string | undefined;
	workspaceIdParam: string | undefined;
	entityIdParam: string | undefined;
}
const getImporterContext = ({
	importTypeParam,
	workspaceIdParam,
	entityIdParam,
}: ImporterContextParams): ImporterContext | null => {
	if (!importTypeParam || !supportedImportTypesFromQueryParam.has(importTypeParam)) return null;

	return {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		importType: importTypeParam as ImportType,
		workspaceId: workspaceIdParam || null,
		entityId: entityIdParam || null,
	};
};

export const useImporterContextFromQueryParams = (): ImporterContext | null => {
	const [{ location }, { replace }] = useRouter();
	const [importTypeParam] = useQueryParam(IMPORT_TYPE_PARAM_KEY);
	const [workspaceIdParam] = useQueryParam(WORKSPACE_ID_PARAM_KEY);
	const [entityIdParam] = useQueryParam(ENTITY_ID_PARAM_KEY);

	const importerContext = useMemo(
		() => getImporterContext({ importTypeParam, workspaceIdParam, entityIdParam }),
		[entityIdParam, importTypeParam, workspaceIdParam],
	);

	useEffect(() => {
		if (importerContext) {
			// reset query params to avoid always defaulting to these when exiting the importer and the navigating back
			replace(location.pathname);
		}
	}, [importerContext, location.pathname, replace]);

	return importerContext;
};
